/**
 * Infrastructure status layer renderer.
 * internet-outages: ScatterplotLayer with pulsing red dots for active outages.
 * cable-advisories: ScatterplotLayer colored by cable health status.
 */

import { ScatterplotLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent } from '@/sources/base-source';

/** Layer IDs handled by this renderer. */
export const INFRA_STATUS_LAYER_IDS = new Set([
  'internet-outages', 'cable-advisories', 'repair-ships',
  'traffic-anomalies', 'ddos-locations',
]);

/** Outage color — matching WorldMonitor (flat red). */
const OUTAGE_COLOR: [number, number, number] = [255, 50, 50];

/** Cable advisory colors — matching WorldMonitor. */
const CABLE_ADVISORY_COLORS: Record<string, [number, number, number, number]> = {
  fault: [255, 50, 50, 220],        // red — matching WM
  degraded: [255, 200, 0, 200],     // yellow — matching WM
  maintenance: [255, 200, 0, 200],  // yellow — matching WM
  advisory: [255, 200, 0, 200],     // yellow — matching WM
};

const DEFAULT_CABLE_ADVISORY_COLOR: [number, number, number, number] = [255, 200, 0, 200];

/** Get cable status from event metadata. */
function getCableStatus(event: NormalizedEvent): string {
  const raw = event.raw as Record<string, unknown> | undefined;
  const status = raw?.cableStatus ?? raw?.status;
  return typeof status === 'string' ? status : 'advisory';
}

/** Create layers for internet outage events — matching WorldMonitor. */
function createOutageLayers(
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  const opacity = Math.min(1, 0.4 + zoom * 0.08);

  return [new ScatterplotLayer<NormalizedEvent>({
    id: 'outage-dots',
    data: events,
    pickable: true,
    filled: true,
    radiusMinPixels: 6,
    radiusMaxPixels: 18,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getFillColor: [...OUTAGE_COLOR, 180] as [number, number, number, number],
    getRadius: 20000,
    opacity,
  })];
}

/** Create layers for cable advisory events — matching WorldMonitor. */
function createCableAdvisoryLayers(
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  const opacity = Math.min(1, 0.3 + zoom * 0.1);

  return [
    new ScatterplotLayer<NormalizedEvent>({
      id: 'cable-advisory-dots',
      data: events,
      pickable: true,
      filled: true,
      stroked: true,
      radiusMinPixels: 5,
      radiusMaxPixels: 12,
      getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
      getFillColor: (d) => {
        const status = getCableStatus(d);
        return CABLE_ADVISORY_COLORS[status] ?? DEFAULT_CABLE_ADVISORY_COLOR;
      },
      getLineColor: [0, 200, 255, 200],   // cyan outline — matching WM
      lineWidthMinPixels: 2,
      getRadius: 10000,
      opacity,
      updateTriggers: {
        getFillColor: [events.length],
      },
    }),
  ];
}

/** Create layers for repair ship tracking — matching WorldMonitor. */
function createRepairShipLayers(
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  const opacity = Math.min(1, 0.3 + zoom * 0.1);

  return [
    new ScatterplotLayer<NormalizedEvent>({
      id: 'repair-ships-dots',
      data: events,
      pickable: true,
      filled: true,
      radiusMinPixels: 4,
      radiusMaxPixels: 10,
      getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
      getFillColor: [0, 255, 200, 200],   // teal — matching WM
      getRadius: 8000,
      opacity,
    }),
  ];
}

/** Create deck.gl layers for infrastructure status data. */
/**
 * Traffic anomalies — the amber middle hue of WM's outages triad
 * (red outage / amber anomaly / purple DDoS). Constant 30000m radius,
 * deliberately 1.5x the outage radius so anomalies read as a larger,
 * softer ring behind hard outages (WM DeckGLMap.ts:3472-3484).
 */
function createTrafficAnomalyLayers(events: NormalizedEvent[]): Layer[] {
  if (events.length === 0) return [];

  return [new ScatterplotLayer<NormalizedEvent>({
    id: 'traffic-anomalies-layer',
    data: events,
    pickable: true,
    filled: true,
    radiusUnits: 'meters' as const,
    radiusMinPixels: 5,
    radiusMaxPixels: 14,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getFillColor: [255, 160, 0, 200],
    getRadius: 30_000,
  })];
}

/**
 * DDoS locations — purple, with WM's only magnitude-scaled radius in the
 * infrastructure group: attack share drives dot size
 * (WM DeckGLMap.ts:3486-3498).
 */
function createDdosLayers(events: NormalizedEvent[]): Layer[] {
  if (events.length === 0) return [];

  return [new ScatterplotLayer<NormalizedEvent>({
    id: 'ddos-locations-layer',
    data: events,
    pickable: true,
    filled: true,
    radiusUnits: 'meters' as const,
    radiusMinPixels: 5,
    radiusMaxPixels: 16,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getFillColor: [180, 0, 255, 200],
    getRadius: (d) => {
      const raw = d.raw as Record<string, unknown> | undefined;
      const pct = typeof raw?.percentage === 'number' ? raw.percentage : 0;
      return 20_000 + pct * 800;
    },
    updateTriggers: {
      getRadius: [events.length],
    },
  })];
}

export function createInfraStatusLayers(
  layerId: string,
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  if (layerId === 'internet-outages') {
    return createOutageLayers(events, zoom);
  }
  if (layerId === 'repair-ships') {
    return createRepairShipLayers(events, zoom);
  }
  if (layerId === 'traffic-anomalies') {
    return createTrafficAnomalyLayers(events);
  }
  if (layerId === 'ddos-locations') {
    return createDdosLayers(events);
  }
  return createCableAdvisoryLayers(events, zoom);
}
